import { DateTime } from "luxon";
import { TBookingType, TMainTrackAgeFilters, TObjFromArray } from "../interfaces";
import { BREAK_BOOKING_NAME, NUMBER_OF_SEATS_IN_A_BOOKING_JUNIOR_TRACK, NUMBER_OF_SEATS_IN_A_BOOKING_MAIN_TRACK, NUMBER_OF_SEATS_IN_A_BOOKING_MINI_TRACK, NUMBER_OF_SEATS_IN_A_VR } from "../constants/Dashboard";
import { IOrder } from "shopify-api-node";
import { DocumentData, QuerySnapshot } from "firebase/firestore";
import { ISessionWithBookings } from "../interfaces/Session";
import { getDriverCount } from "../components/Utilities/dashboard";
import { TrackConfigurations } from "../constants";

export function ValidateEmail(email: string) {
  return new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  ).test(email);
}

export function ValidatePhone(phone: string) {
  return new RegExp(
    /^(\+\d{1,2}\s?)?1?-?.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
  ).test(phone);
}

export const getDatetimeFromSku = (sku: string, track: TBookingType) => {
  let raceDate = sku?.split(".")?.[0];
  if (raceDate) {
    if (track === "MINI_TRACK") {
      raceDate = raceDate?.slice?.(1);
    }
    if (raceDate && !isNaN(raceDate as any)) {
      const dt = DateTime.fromFormat(raceDate, "yyMMddHHmm", {
        zone: "Australia/Sydney",
      });

      return dt;
    }
  }
  return null;
};

export const getTrackFromSku: (sku: string) => TBookingType = (sku) => {
  if (sku && sku.includes("VR")) {
    return "VR";
  } else if (sku && sku.includes("MI")) {
    return "MINI_TRACK";
  } else if (sku && sku.includes("J10")){
    return "JUNIOR_TRACK"
  } else if (sku && sku.includes("I10")){
    return "INTERMEDIATE_TRACK";
  } else if (sku && sku.includes("BK10")){
    return "BATTLE_KART";
  }
  return "MAIN_TRACK";
};

export const isBreakOrder = (order: IOrder) => {
  return order?.customer?.first_name
    ?.toLowerCase()
    ?.includes?.(BREAK_BOOKING_NAME.toLowerCase());
};

export function setPropertyValue<T, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K]
): void {
  obj[key] = value;
}

export function deepCopy<T>(obj: T): T {
  if (typeof obj !== "object" || obj === null) {
    return obj as T; // Return primitive types, null, and undefined as is
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCopy(item)) as T; // Recursively deep copy array elements
  }

  // Recursively deep copy object properties
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, deepCopy(value)])
  ) as T;
}

export function handleQuerySnapshot<T>(
  querySnapshot: QuerySnapshot<DocumentData>
): TObjFromArray<T> {
  const data: TObjFromArray<T> = {};
  querySnapshot.docs.forEach((doc) => {
    data[doc.id] = doc.data() as T;
  });
  return data;
}

export function formatPhoneNumber(number: string) {
  if (number.startsWith("04")) {
    return "614" + number.slice(2);
  }
  if (number.startsWith("+61")) {
    return number.slice(1);
  }
  if (number.startsWith("6104")) {
    return "614" + number.slice(4);
  }
  if (number.startsWith("4") && number.length === 9) {
    return "61" + number;
  }
  return number;
}

export function formatPhoneNumberForPodium(number: string) {
  return "+" + formatPhoneNumber(number);
}

export const getPossiblePhoneNumbers = (number: string) => [
  number,
  formatPhoneNumber(number),
  formatPhoneNumberForPodium(number),
  `0${number}`,
  `06${number}`,
  `61${number}`,
  `+61${number}`,
];

export function createBatches<T>(array: T[], batchSize: number): T[][] {
  const batches: T[][] = [];

  for (let i = 0; i < array.length; i += batchSize) {
    const batch = array.slice(i, i + batchSize);
    batches.push(batch);
  }

  return batches;
}
export const getAgeInYears = (dt: DateTime) => {
  return Math.abs(
    Math.ceil(
      dt.diffNow('years').years
    )
  )
}

export const getProductHandle = (date: Date, track: TBookingType) => {
  const dateString = DateTime.fromJSDate(date, {
    zone: "Australia/Sydney",
  }).toFormat("yyLLdd");
  if (track === "MAIN_TRACK") {
    return `${dateString}mt`;
  } else if (track === "MINI_TRACK") {
    return `0${dateString}mi`;
  } else if (track === "VR") {
    return `${dateString}vr`;
  } else if (track === "JUNIOR_TRACK") {
    return `${dateString}jt`;
  } else if (track === "INTERMEDIATE_TRACK") {
    return `${dateString}it`;
  } else if (track === "BATTLE_KART") {
    return `${dateString}bk`;
  }
  return undefined;
};

export const getDateTimeFromHandle = (handle: string): DateTime | null => {
  // Remove leading 0 (for mini track) and slice off last 2 characters
  const dateString = handle.replace(/^0/, '').slice(0, -2);
  
  if (dateString && dateString.length === 6) {
    const dt =  DateTime.fromFormat(dateString, "yyLLdd", {
      zone: "Australia/Sydney"
    });
    if(dt.isValid){
      return dt;
    }
  }
  
  return null;
};

export const getMaxNumberOfSession = (type: TBookingType) => {
  if(type === "MAIN_TRACK"){
    return NUMBER_OF_SEATS_IN_A_BOOKING_MAIN_TRACK
  }else if(type === "MINI_TRACK"){
    return NUMBER_OF_SEATS_IN_A_BOOKING_MINI_TRACK
  }else if(type === "JUNIOR_TRACK"){
    return NUMBER_OF_SEATS_IN_A_BOOKING_JUNIOR_TRACK
  }
  return NUMBER_OF_SEATS_IN_A_VR
}

export const getTrackDisplayName = (type: TBookingType)=>{
  if(type === "MAIN_TRACK"){
    return "Adult Track"
  }else if(type === "MINI_TRACK"){
    return "Mini Track"
  }else if(type === "JUNIOR_TRACK"){
    return "Junior Track"
  }else if(type === "INTERMEDIATE_TRACK"){
    return "Intermediate Track"
  }else if(type === "BATTLE_KART"){
    return "Battle Kart"
  }else{
    return "Virtual Reality"
  }
}

export const getTrackColor = (type: TBookingType) =>{
  if(type === "MAIN_TRACK"){
    return "#FF00FF"
  }else if(type === "MINI_TRACK"){
    return "#9D62FE"
  }else if(type === "JUNIOR_TRACK"){
    return "#41BDFE"
  }else if(type === "INTERMEDIATE_TRACK"){
    return "#41BDFE"
  }else{
    return "#41BDFE"
  }
}

export const getOrderOccasion = (order: IOrder) => {
  const tags = order.tags.split(",")
  const occasionTag = tags.find(tag=>tag.includes("OCCASION:"))
  return occasionTag?.split(":")?.[1]
}

export const copyToClipboard = (content: string, onSuccess?: () => void, onError?: (error: Error) => void) => {
  if (window?.navigator?.clipboard) {
    window.navigator.clipboard.writeText(content).then(() => {
      onSuccess && onSuccess()
    }).catch((error) => {
      console.log("copying error", error);
      onError && onError(error)
    });
  } else {
    console.log("copying error", "Clipboard API not supported");
    onError && onError(new Error("Clipboard API not supported"))
  }
}

export const getLiveUsersRegistrationContent = (count: number) => {
  return `${count} ${count === 1 ? "person" : "people"} currently registering`;
}

export const applyAgeFilter = (sessions: ISessionWithBookings[], ageFilter: TMainTrackAgeFilters, track: TBookingType) => {
  return sessions.filter((session) => {
    let valid = true;
    if (track === "MAIN_TRACK") {
      if (ageFilter !== TMainTrackAgeFilters.ALL && !(session.option1 || "").toLowerCase().includes(ageFilter.toLowerCase())) {
        valid = false;
      }
    }
    return valid;
  })
}

export const getOrderIdsFromSessions = (sessions: ISessionWithBookings[]) => {
  return sessions.map((session) => session.bookings.map((booking) => booking.order)).flat().map((order) => String(order.id))
}

export const getSessionSubTrackId = (track: TBookingType) => {
  if(track === "MAIN_TRACK"){
    return 1
  }else if(track === "MINI_TRACK"){
    return 2
  }else if(track === "JUNIOR_TRACK"){
    return 5
  }
  return 3
}

export const getSessionBackgroundColor = (session: ISessionWithBookings) => {
  // First check if there are any bookings and determine color based on seats sold
  const totalSeatsSold = session.bookings.reduce((total, booking) => {

    const driverCount = getDriverCount(booking);
    return total + (driverCount || 0);
  }, 0);

  if (totalSeatsSold === 0) {
    return session.inventory_quantity === 0 ? "bg-[#FFD8D8]" : "bg-[#DBFFD1]"; // If race is closed off then pink, else green
  }

  return "";
};

// Gets the track configuration name from its UUID
export const getTrackConfigurationName = (uuid?: string): string => {
  if (!uuid) return "Unknown";
  const config = TrackConfigurations.find(config => config.uuid === uuid);
  return config?.name || "Unknown";
}

export const getEnvironment = ()=>{
  return window.location.hostname === "app.hyperkarting.com.au" ? "production" : "development";
}